import { firebase } from "./firebase-config";
import axiosInstance from "./axios";
import {
  confirmPasswordReset,
  createUserWithEmailAndPassword,
  getAuth,
  getIdToken,
  onAuthStateChanged,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signOut,
} from "firebase/auth";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  query,
  setDoc,
  updateDoc,
  where,
} from "firebase/firestore";
import { getLoginToken } from "./storage";
import {
  APP_STORE_ID,
  BaseUrl,
  DOMAINURL,
  EMP_DOMAIN,
  PLAY_STORE_ID,
} from "./config";
import {
  getDownloadURL,
  getStorage,
  ref,
  uploadBytesResumable,
} from "firebase/storage";
import { incrementNotificationLimits } from "./apiservices/employee";
const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
// const firestore = firebase.firestore();
// const settings = { timestampsInSnapshots: true };
// firestore.settings(settings);

/**
 * so this function is called when the authentication state changes
 * in the application, a side effect of that is that we need to get
 * the rest of the user data from the user collection, that is
 * done with the _handleAuthedUser callback
 */
const auth = getAuth();
const storage = getStorage();

export const authCheck = async (_handleAuthedUser) => {
  return new Promise((resolve) => {
    onAuthStateChanged(auth, async (user) => {
      if (user) {
        return resolve(await _handleAuthedUser(user));
      } else {
        _handleAuthedUser(null);
        return resolve(null);
      }
    });
  });
};

/**
 *
 * @param {*} email
 * @param {*} password
 */
export const loginWithEmail = async (email, password) => {
  try {
    const userCredential = await signInWithEmailAndPassword(
      auth,
      email,
      password
    );
    const user = userCredential.user;
    const token = await user.getIdToken();

    const userDoc = await getDoc(doc(firebase, "users", user.uid));
    const role = userDoc.exists() ? userDoc?.data()?.role : null;

    return {
      userId: user?.uid,
      user,
      token,
      role,
    };
  } catch (error) {
    return error;
  }
};
/**
 *
 * @returns data of current logged in user from users collection
 */
export const getCurrentUser = async () => {
  const user = auth.currentUser;
  let token = null;
  let userData = {};

  if (user) {
    token = await getIdToken(user);

    const userDocRef = doc(firebase, "users", user.uid);
    const docSnap = await getDoc(userDocRef);

    if (docSnap.exists()) {
      userData = { ...docSnap.data(), id: user.uid };

      const userRole = userData?.role;
      if (userRole !== "companyUser" && userRole !== "superUser") {
        const compUserDocRef = doc(firebase, "users", userData?.companyId);
        const docCompSnap = await getDoc(compUserDocRef);
        const compUserData = docCompSnap.data();
        userData = {
          ...userData,
          customer: compUserData?.customer,
          subscriptions: compUserData?.subscriptions || null,
        };
      }
    } else {
      console.log("No such document!", user.uid);
    }
  }
  return { user, userData, token };
};

export const getCurrentUserToken = async (user) => {
  let token = await user.getIdToken();
  return token;
};
/**
 * Log out user
 */
export const logOut = () => {
  return signOut(auth);
};

export const emailLinkSent = async (email, firstname) => {
  return axiosInstance.post(`${BaseUrl}sendEmailVerifyLink`, {
    email,
    displayName: firstname,
  });
};

export const forgotPasswordAgainemailLinkSent = async (email) => {
  return axiosInstance.post(`${BaseUrl}sendResetPasswordLink`, {
    email,
    displayName: null,
    role: null,
    employeerole: null,
  });
};

export const updateEmailResendLink = async (userId, role) => {
  if (role === "ClientTeam") {
    return axiosInstance.post(`${BaseUrl}employeeEmailVerifyLink`, {
      oldEmail: null,
      newEmail: null,
      displayName: null,
      userId: userId,
    });
  } else {
    return axiosInstance.post(`${BaseUrl}emailVerifyLink`, {
      oldEmail: null,
      newEmail: null,
      displayName: null,
      userId: userId,
    });
  }
};

/**
 *
 * @param {*} userInfo.lastName
 * @param {*} userInfo.firstName
 * @param {*} userInfo.email
 * @param {*} userInfo.password
 */

export const registerUser = async (userInfo, existingUser = null) => {
  let newcreateduser = {};
  try {
    const {
      companyName,
      firstName,
      lastName,
      email,
      username,
      phoneNumber,
      website,
      peoples,
      industry,
      password,
      confirmPassword,
      createdAt,
      role,
      promocode,
    } = userInfo;
    const priceId =
      process.env.REACT_APP_CUSTOM_ENV === "prod"
        ? "price_1RsdExHPX5Uwv3eVvXeq6n3a"
        : process.env.REACT_APP_CUSTOM_ENV === "dev"
        ? "price_1Roe9PH54HzvHwSdFEwomMT8"
        : "price_1RoeHcHG9KfCFcBtNO24WZi1";

    if (existingUser && existingUser !== null) {
      const userRef = doc(firebase, "users", existingUser?.id);
      await updateDoc(userRef, {
        email: email || "",
        firstName: firstName || "",
        lastName: lastName || "",
        role: role || existingUser?.role || "Employee",
        phoneNumber: phoneNumber || "",
        companyName: companyName || "",
        website: website || "",
        peoples: peoples || "",
        industry: industry || "",
        password: password || "",
        confirmPassword: confirmPassword || "",
        promocode: promocode || "",
        updatedAt: new Date(),
        verified: false,
      });

      await axiosInstance.post(`${BaseUrl}sendEmailVerifyLink`, {
        email,
        displayName: companyName,
      });

      return {
        status: true,
        message:
          "Existing user updated and verification email sent successfully.",
        existingUser,
        firstName,
        lastName,
      };
    }

    const userCredential = await createUserWithEmailAndPassword(
      auth,
      userInfo.email,
      userInfo.password
    );
    newcreateduser = userCredential;

    const [firestoreWrite, subscriptionRes, emailVerifyRes] = await Promise.all(
      [
        setDoc(doc(firebase, "users", newcreateduser.user.uid), {
          email: email || "",
          firstName: firstName || "",
          lastName: lastName || "",
          role: role || "Employee",
          createdAt: createdAt || "",
          phoneNumber: phoneNumber || "",
          companyName: companyName || "",
          website: website || "",
          peoples: peoples || "",
          industry: industry || "",
          password: password || "",
          confirmPassword: confirmPassword || "",
          promocode: promocode || "",
          verified: false,
        }),

        axiosInstance.post(`${BaseUrl}createSubscription`, {
          email,
          name: companyName,
          phone: phoneNumber,
          priceId: priceId,
          promocode: promocode,
        }),

        axiosInstance.post(`${BaseUrl}sendEmailVerifyLink`, {
          email,
          displayName: companyName,
        }),
      ]
    );

    if (subscriptionRes?.data?.status === true) {
      await delay(4000);
      console.log("Subscription Created:", subscriptionRes.data);
      return { ...newcreateduser.user, firstName, lastName };
    }
  } catch (error) {
    console.error("[registerUser error]", {
      code: error.code,
      message: error.message,
    });

    if (error.code === "auth/email-already-in-use") {
      return { status: false, message: "Email is already registered" };
    }

    return { status: false, message: error.message || "Something went wrong" };
  }
};

/**
 * register new user to firebase auth and users collection (not used for now manage from cloud function)
 * @param {*} newUser
 * @returns
 */
export async function firebasesignupUser(newUser) {
  try {
    const userRef = doc(firebase, "users", newUser.email);
    const docSnap = await getDoc(userRef);

    if (docSnap.exists()) {
      return { status: false, message: "This username is already taken" };
    } else {
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        newUser.email,
        newUser.password
      );
      const newCreatedUser = userCredential.user;

      const { email, firstName, lastName, role, createdAt, phonenumber } =
        newUser;

      await setDoc(doc(firebase, "users", newCreatedUser.uid), {
        email: email || "",
        firstName: firstName || "",
        lastName: lastName || "",
        role: role || "",
        createdAt: createdAt || "",
        phoneNumber: phonenumber || "",
      });

      return { ...newCreatedUser, firstName, lastName };
    }
  } catch (error) {
    return { status: false, error: error.message };
  }
}

/**
 * Sent Forgot password email for give email user
 * @param {*} email
 * @param {*} onSuccess
 * @param {*} onError
 */
export const forgotPasswordUser = async (email, onSuccess, onError) => {
  try {
    const userQuery = query(
      collection(firebase, "users"),
      where("email", "==", email),
      limit(1)
    );
    const userSnapshot = await getDocs(userQuery);
    if (userSnapshot.empty) {
      if (onError) {
        onError({ status: false, message: "No User found with this email" });
      }
      return;
    }

    const user = userSnapshot.docs[0].data();

    if (user?.status === false) {
      if (onError) {
        onError({
          status: false,
          message:
            "Email ID no longer active. Please contact your administrator for assistance.",
        });
      }
      return;
    }

    if (user?.verified === false) {
      if (onError) {
        onError({
          status: false,
          message:
            "Your account hasn’t been verified yet. Please verify your email before resetting your password.",
        });
      }
      return;
    }

    onSuccess?.({ status: true, message: "Account found" });

    await axiosInstance.post(`${BaseUrl}sendResetPasswordLink`, {
      email,
      displayName: user?.firstName ?? "",
      role: Array.isArray(user?.role)
        ? user.role.includes("admin")
          ? "admin"
          : user.role[0]
        : user?.role,
      employeerole: user?.employeerole ?? "",
    });

    onSuccess({ status: true, message: "Password reset email sent!" });
    // if (user.role === 'Employee') {
    //   await sendPasswordResetEmail(auth, email, { url: `https://devemp.proteams.io/updatepassword` });
    //   if (onSuccess) {
    //     onSuccess({ status: true, message: 'Password reset email sent!' });
    //   }
    // } else {
    //   await sendPasswordResetEmail(auth, email, { url: `https://${DOMAINURL}/updatepassword` });
    //   if (onSuccess) {
    //     onSuccess({ status: true, message: 'Password reset email sent!' });
    //   }
    // }
  } catch (error) {
    const errorMessage = error.message;
    if (onError) {
      onError({ status: false, message: errorMessage });
    }
  }
};

export const forgotPasswordAdmin = async (
  email,
  phoneNumber,
  companyId,
  onSuccess,
  onError
) => {
  try {
    const userQuery = query(
      collection(firebase, "users"),
      where("email", "==", email)
    );
    const userSnapshot = await getDocs(userQuery);

    if (userSnapshot.empty) {
      if (onError) {
        onError({ status: false, message: "No User found with this email" });
      }
      return;
    }

    let user = {};
    userSnapshot.forEach((doc) => {
      user = doc.data();
    });

    let actionCodeSettings = {
      url: `https://${DOMAINURL}/`,
    };

    if (user.employeerole === "Client Team") {
      actionCodeSettings = {};
    }

    await sendPasswordResetEmail(auth, email, actionCodeSettings);

    const authHeaders = { Authorization: `Bearer ${getLoginToken()}` };
    await axiosInstance.post(
      `${BaseUrl}sendSmsText`,
      {
        phoneNumber: [phoneNumber],
        textMessage: `Hello,\n\nYour ProTeams password has been reset. Please check ${email} for your password reset link`,
      },
      {
        headers: {
          "Content-Type": "application/json",
          withCredentials: true,
          ...authHeaders,
        },
      }
    );

    if (companyId) {
      await incrementNotificationLimits(companyId);
    }

    if (onSuccess) {
      onSuccess({ status: true, message: "Password reset email sent!" });
    }
  } catch (error) {
    const errorMessage = error.message;
    if (onError) {
      onError({ status: false, message: errorMessage });
    }
  }
};

export const ResendCMSLink = async (
  email,
  phoneNumber,
  firstName,
  password,
  role,
  onSuccess,
  onError
) => {
  try {
    const authHeaders = { Authorization: `Bearer ${getLoginToken()}` };

    const emailRequest = axiosInstance.post(
      `${BaseUrl}sendEmailData`,
      {
        email: email,
        subject: `You have been invited the ProTeams Web Portal `,
        contenttxt: `
          <div style="font-family: Arial, sans-serif; color: #333; max-width: 600px; margin: 0 auto; background-color: #ffffff; border-radius: 10px; overflow: hidden;">
            <div style="background-color:#f4f6f8; padding:20px; text-align:center;">
              <img
                src="https://${DOMAINURL}/csmGrayTextLogin.png"
                alt="ProTeams Logo"
                width="180"
                style="display:block; margin:0 auto; max-width:180px; width:100%; height:auto; border:0;"
              />
            </div>

            <div style="padding:25px 20px;">

            <p><strong>Welcome to ProTeams</strong></p>

            <p>Hello ${firstName},</p>

            <p>
              Welcome to the ProTeams Web Portal! You can access the web platform using the credentials below:
            </p>

            <p>
              Here are your login credentials:
            </p>

             <p><strong>User name:</strong><br />${email}</p>
             <p><strong>Password:</strong><br />${password}</p>
             <p><strong>Role:</strong><br />${role}</p>

             <div style="text-align: center; margin: 25px 0;">
              <a href="https://${DOMAINURL}/login" 
                style="background-color: #078037; color: #fff; padding: 12px 25px; text-decoration: none; border-radius: 5px; font-weight: bold; display: inline-block;">
                  Go to ProTeams Web Portal
              </a>
             </div>

             <p style="font-size:14px; color:#666; margin-bottom:20px;">
                You can view the ProTeams
                <a href="https://proteams.io/privacy-policy/" target="_blank" style="color:#1a73e8; text-decoration:underline;">
                  Privacy Policy
                </a>.
              </p>

            <hr style="border:none; border-top:1px solid #e5e7eb; margin:30px 0;">

            <p style="text-align:center; font-size:13px; color:#666; margin:0;">
              For assistance, feel free to <a href="mailto:support@proteams.io" style="color:#1a73e8; text-decoration:underline;">contact support</a>.<br>
              © ${new Date().getFullYear()} ProTeams. All rights reserved.
            </p>
          </div>
          </div>
        `,
      },
      {
        headers: {
          "Content-Type": "application/json",
          withCredentials: true,
          ...authHeaders,
        },
      }
    );
    emailRequest
      .then(() => {
        onSuccess?.({ status: true, message: "Resend Invitation sent!" });
      })
      .catch((err) => {
        onError?.({ status: false, message: err.message || "Email failed" });
      });

    return { status: true, message: "Sending invitation in background" };
  } catch (error) {
    if (onError) {
      onError({ status: false, message: error.message });
    }
  }
};

export const ResendAppLink = async (
  email,
  phoneNumber,
  firstName,
  password,
  contactName,
  companyName,
  companyId,
  onSuccess,
  onError
) => {
  try {
    const authHeaders = { Authorization: `Bearer ${getLoginToken()}` };

    const emailRequest = axiosInstance.post(
      `${BaseUrl}sendEmailData`,
      {
        email: email,
        subject: `You have been invited the ProTeams Mobile App`,
        contenttxt: `
            <div style="font-family: Arial, sans-serif; color: #333; max-width: 600px; margin: 0 auto; background-color: #ffffff; border-radius: 10px; overflow: hidden;">
            <div style="background-color:#f4f6f8; padding:20px; text-align:center;">
              <img
                src="https://${DOMAINURL}/csmGrayTextLogin.png"
                alt="ProTeams Logo"
                width="180"
                style="display:block; margin:0 auto; max-width:180px; width:100%; height:auto; border:0;"
              />
            </div>

            <div style="padding:25px 20px;">

              <p style="margin-bottom:15px;">Hi ${firstName},</p>

              <p style="margin-bottom:15px;">
                You've been invited to the <strong>ProTeams</strong> mobile app
                by <strong>${contactName}</strong> from <strong>${companyName}</strong>.
              </p>

              <p style="margin-bottom:15px;">
                <strong>ProTeams</strong> is an industry-leading software service that
                enables professional service teams to work more efficiently and effectively together.
              </p>

              <p style="margin-bottom:20px;">
                This software runs on both iOS and Android devices and is free for end users.
                Download the app using the options below:
              </p>

              <div style="text-align:center; margin:25px 0;">
                <a href="https://${APP_STORE_ID}" target="_blank">
                  <img src="https://${DOMAINURL}/appstore.png" style="height:50px; margin:5px;" />
                </a>
                <a href="https://${PLAY_STORE_ID}" target="_blank">
                  <img src="https://${DOMAINURL}/playstore.png" style="height:50px; margin:5px;" />
                </a>

                <p style="margin-top:10px; font-size:14px; color:#666;">
                  Available on iPhone and all Android phones
                </p>
              </div>

              <p style="margin-bottom:10px;"><strong>User name:</strong><br />${email}</p>
              <p style="margin-bottom:20px;"><strong>Password:</strong><br />${password}</p>

              <p style="font-size:14px; color:#666; margin-bottom:15px;">
                If you have any questions, please contact the company that invited you.
              </p>

              <p style="font-size:14px; color:#666; margin-bottom:20px;">
                You can view the ProTeams
                <a href="https://proteams.io/privacy-policy/" target="_blank" style="color:#1a73e8; text-decoration:underline;">
                  Privacy Policy
                </a>.
              </p>

              <hr style="border:none; border-top:1px solid #e5e7eb; margin:30px 0;">

              <p style="text-align:center; font-size:13px; color:#666; margin:0;">
                For assistance, feel free to <a href="mailto:support@proteams.io" style="color:#1a73e8; text-decoration:underline;">contact support</a>.<br>
                © ${new Date().getFullYear()} ProTeams. All rights reserved.
              </p>
            </div>
          </div>
        `,
      },
      {
        headers: {
          "Content-Type": "application/json",
          withCredentials: true,
          ...authHeaders,
        },
      }
    );

    const smsRequest = axiosInstance.post(
      `${BaseUrl}sendSmsText`,
      {
        phoneNumber: [phoneNumber],
        textMessage: `Hello ${firstName},\n\nYou're invited to the ProTeams App! Download it - https://${EMP_DOMAIN}/apps/\n\nYour ID & password were sent to ${email}`,
      },
      {
        headers: {
          "Content-Type": "application/json",
          withCredentials: true,
          ...authHeaders,
        },
      }
    );

    Promise.allSettled([emailRequest, smsRequest])
      .then(async(results) => {
        const emailStatus = results[0].status === "fulfilled";
        const smsStatus = results[1].status === "fulfilled";

        if (companyId) {
          await incrementNotificationLimits(companyId);
        }

        if (emailStatus || smsStatus) {
          onSuccess?.({ status: true, message: "Resend Invitation sent!" });
        } else {
          onError?.({ status: false, message: "Both email and SMS failed" });
        }
      })
      .catch((err) => {
        onError?.({ status: false, message: err.message });
      });

    return { status: true, message: "Sending invitation in background" };
  } catch (error) {
    if (onError) {
      onError({ status: false, message: error.message });
    }
    return { status: false, message: error.message };
  }
};

/**
 * Returns Current login user profile data
 */
export const getUserProfile = async (token, onSuccess, onError) => {
  try {
    const user = auth.currentUser;

    if (user) {
      const userRef = doc(firebase, "users", user.uid);
      const docSnap = await getDoc(userRef);

      if (docSnap.exists()) {
        onSuccess &&
          onSuccess({
            status: true,
            data: { ...docSnap.data(), id: user.uid },
          });
      } else {
        onError && onError({ status: false, data: "No such document!" });
      }
    } else {
      onError && onError({ status: false, data: "No user logged in!" });
    }
  } catch (error) {
    onError &&
      onError({ status: false, data: "Error retrieving user profile!", error });
  }
};

/**
 *
 * @param {*} blob
 */
export const uploadImage = (blob) => {
  return new Promise((resolve, reject) => {
    const currentUserId = auth.currentUser?.uid;

    const imageRef = ref(
      storage,
      `${currentUserId}/${new Date().getTime()}-${currentUserId}.jpeg`
    );

    const uploadTask = uploadBytesResumable(imageRef, blob);

    uploadTask.on(
      "state_changed",
      (snapshot) =>
        console.log((snapshot.bytesTransferred / snapshot.totalBytes) * 100),

      (error) => {
        console.log("error", error);
        reject(error);
      },
      async () => {
        try {
          const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
          const metadata = uploadTask.snapshot.metadata;
          resolve({
            url: downloadURL,
            contentType: metadata.contentType,
            name: metadata.name,
            size: metadata.size,
          });
        } catch (error) {
          reject(error);
        }
      }
    );
  });
};

export const updatepasswordem = (code, newPassword, onSuccess, onError) => {
  confirmPasswordReset(auth, code, newPassword)
    .then(() => {
      onSuccess && onSuccess({ status: true, message: "Password Updated!" });
    })
    .catch((error) => {
      let friendlyMessage = "Something went wrong. Please try again.";

      switch (error.code) {
        case "auth/expired-action-code":
          friendlyMessage =
            "This reset link has expired. Please request a new one.";
          break;
        case "auth/invalid-action-code":
          friendlyMessage =
            "This reset link is invalid or has already been used.";
          break;
        case "auth/user-not-found":
          friendlyMessage =
            "No account found with this email. Please check or sign up.";
          break;
        case "auth/weak-password":
          friendlyMessage =
            "Password is too weak. Please choose a stronger password.";
          break;
        default:
          friendlyMessage = "Unable to reset your password. Please try again.";
      }
      onError && onError({ status: false, message: friendlyMessage });
    });
};
